import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { ArenaCanvas } from "../three/Arena";
import TeamStage, { avatarPosition } from "../three/TeamStage";
import Orb from "../three/Orb";
import { ConfettiBurst } from "../three/Confetti";
import HtmlBoard from "./HtmlBoard";
import ResultsView from "./ResultsView";
import { useGame, useGameEvent } from "../game/GameProvider";
import { errorToastStyle, infoToastStyle } from "../utils/utils";
import { teamOf } from "../game/theme";
import { sfx, setMuted, isMuted } from "../game/sounds";

// Orbs fly up and away toward the HTML board's on-screen position.
const ORB_TARGET = [0, 5.8, -6];
// Team platforms are pulled toward the camera so throws read clearly.
const TEAM_Z = 1.6;

// Match a correct answer event to a board tile (server is authoritative;
// this only picks which tile flips).
function findTileIndex(answers, revealed, evt, multiplier) {
  const sub = (evt.submitted_answer || "").trim().toLowerCase();
  const open = (pred) => answers.findIndex((a, i) => !revealed[i] && pred(a));
  let idx = open((a) => (a.ans || "").toLowerCase() === sub);
  if (idx < 0 && sub)
    idx = open((a) => {
      const ans = (a.ans || "").toLowerCase();
      return ans.includes(sub) || sub.includes(ans);
    });
  if (idx < 0 && evt.points)
    idx = open(
      (a) =>
        Number(a.point) === Number(evt.points) ||
        Number(a.point) * multiplier === Number(evt.points)
    );
  if (idx < 0) idx = open(() => true);
  return idx;
}

function TimerRing({ timer, max = 85 }) {
  const R = 24;
  const C = 2 * Math.PI * R;
  const frac = Math.max(0, Math.min(1, timer / max));
  return (
    <div className={`timer-ring ${timer <= 10 ? "urgent" : ""}`}>
      <svg width="56" height="56" viewBox="0 0 56 56">
        <circle cx="28" cy="28" r={R} fill="none" stroke="rgba(126,140,255,0.2)" strokeWidth="5" />
        <circle
          cx="28"
          cy="28"
          r={R}
          fill="none"
          stroke={timer <= 10 ? "var(--danger)" : "var(--gold)"}
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray={`${C * frac} ${C}`}
          style={{ transition: "stroke-dasharray 0.9s linear, stroke 0.3s" }}
        />
      </svg>
      <div className="t-num">{timer}</div>
    </div>
  );
}

function Game() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const roomId = searchParams.get("roomId") || "";
  const paramUserId = searchParams.get("userId") || "";
  const paramTeam = searchParams.get("team") || "";

  const { room, userId: ctxUserId, team: ctxTeam, sendMessage } = useGame();
  const userId = ctxUserId || paramUserId;

  const [roomData, setRoomData] = useState(null);
  const [timer, setTimer] = useState(0);
  const [answerText, setAnswerText] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [gameResult, setGameResult] = useState(null);
  const [orbs, setOrbs] = useState([]);
  const [revealed, setRevealed] = useState([]);
  const [flash, setFlash] = useState({ color: null, n: 0 });
  const [burst, setBurst] = useState(0);
  const [bumps, setBumps] = useState({});
  const [highlightId, setHighlightId] = useState(null);
  const [roundPoints, setRoundPoints] = useState({ left: 0, right: 0 });
  const [muted, setMutedUi] = useState(isMuted());

  const orbSeq = useRef(0);
  const seeded = useRef(false);
  const deadline = useRef(null);

  // Wall-clock game timer: setTimeout chains drift under render load, so we
  // store a deadline and derive the remaining seconds from Date.now().
  const startTimer = useCallback((seconds) => {
    deadline.current = Date.now() + seconds * 1000;
    setTimer(seconds);
  }, []);

  useEffect(() => {
    const iv = setInterval(() => {
      if (deadline.current == null) return;
      const remain = Math.max(
        0,
        Math.ceil((deadline.current - Date.now()) / 1000)
      );
      setTimer((prev) => (prev === remain ? prev : remain));
    }, 200);
    return () => clearInterval(iv);
  }, []);

  // ----- derived state -----
  const players = useMemo(() => roomData?.player_details || [], [roomData]);
  const me = players.find((p) => p.id === userId);
  const isAdmin = !!me?.is_admin;
  const myTeam = me?.team || ctxTeam || paramTeam || "left";

  const idx = roomData?.current_question_index ?? 0;
  const questionCount = roomData?.questions?.length ?? 0;
  const currentQuestion = roomData?.questions?.[idx]?.questions?.[0];
  const prevQuestion = roomData?.questions?.[idx - 1]?.questions?.[0];
  const answers = useMemo(
    () => currentQuestion?.correctAnswers || [],
    [currentQuestion]
  );
  const multiplier = idx + 1;

  const totalPoints = answers.reduce((acc, a) => acc + Number(a.point || 0), 0);
  const leftPct = totalPoints
    ? Math.min(100, (roundPoints.left / totalPoints) * 100)
    : 0;
  const rightPct = totalPoints
    ? Math.min(100, (roundPoints.right / totalPoints) * 100)
    : 0;
  const revealedCount = revealed.filter(Boolean).length;
  const roundWon =
    leftPct >= 100 ||
    rightPct >= 100 ||
    (answers.length > 0 && revealedCount >= answers.length);

  let phase = "loading";
  if (showResults && timer <= 90) phase = "results";
  else if (roomData && timer > 90 && idx !== 0) phase = "summary";
  else if (roomData && timer > 85) phase = "intro";
  else if (roomData) phase = "answer";

  const leftTeam = players.filter((p) => p.team === "left");
  const rightTeam = players.filter((p) => p.team === "right");

  // ----- server events -----
  const applyRoomStatus = useCallback((rs) => {
    const atResults = rs.current_question_index === rs.questions?.length;
    setRoomData(rs);
    if (!atResults) {
      // Reconcile board state from the server: answers may have landed
      // before this page mounted (or after a refresh mid-round).
      const given = (rs.already_given_answers || []).map((a) =>
        String(a).toLowerCase()
      );
      const q = rs.questions?.[rs.current_question_index]?.questions?.[0];
      setRevealed(
        (q?.correctAnswers || []).map((a) =>
          given.includes((a.ans || "").toLowerCase())
        )
      );
      setOrbs([]);
      setRoundPoints({
        left: rs.left_team_points ?? 0,
        right: rs.right_team_points ?? 0,
      });
      setAnswerText("");
      setHighlightId(null);
      startTimer(rs.current_question_index === 0 ? 90 : 100);
    } else {
      startTimer(100);
    }
  }, [startTimer]);

  useGameEvent("roomStatus", applyRoomStatus);

  useGameEvent("result", (gr) => {
    setGameResult(gr || null);
    setShowResults(true);
  });

  useGameEvent("error", (err) => toast.error(err, errorToastStyle));

  useGameEvent("roomClosed", () => {
    toast.info("Room closed", infoToastStyle);
    setTimeout(() => navigate("/"), 900);
  });

  useGameEvent("reconnected", () => {
    sendMessage({ action: "roomStatus", roomId });
  });

  useGameEvent("answer", (evt) => {
    const pid = evt.player_id;
    const evtTeam = evt.player_team === "right" ? "right" : "left";
    const list = evtTeam === "right" ? rightTeam : leftTeam;
    const i = Math.max(0, list.findIndex((p) => p.id === pid));
    const side = evtTeam === "right" ? 1 : -1;
    const [x, y, z] = avatarPosition(side, i);
    const orbId = ++orbSeq.current;
    setOrbs((prev) => [
      ...prev,
      {
        id: orbId,
        from: [x, y + 1.45, z + TEAM_Z],
        to: ORB_TARGET,
        color: teamOf(evtTeam).glow,
        label: evt.submitted_answer,
        data: evt,
      },
    ]);
    setBumps((prev) => ({ ...prev, [pid]: (prev[pid] || 0) + 1 }));
    sfx.whoosh();
  });

  const handleOrbArrive = useCallback(
    (evt, orbId) => {
      setOrbs((prev) => prev.filter((o) => o.id !== orbId));
      const isDup = !!evt.isAlreadyGiven;
      const isCorrect = !!evt.is_correct && !isDup;

      if (isCorrect) {
        if (evt.isKeyAnswer) sfx.keyAnswer();
        else sfx.correct();
        sfx.flip();
        setBurst((b) => b + 1);
        setHighlightId(evt.player_id);
        setRevealed((prev) => {
          const tileIdx = findTileIndex(answers, prev, evt, multiplier);
          if (tileIdx < 0) return prev;
          const next = answers.map((_, i) => !!prev[i]);
          next[tileIdx] = true;
          return next;
        });
      } else if (isDup) {
        sfx.alreadyGiven();
        setFlash((f) => ({ color: "dup", n: f.n + 1 }));
      } else {
        sfx.wrong();
        setFlash((f) => ({ color: "wrong", n: f.n + 1 }));
      }
      setRoundPoints((prev) => ({
        left: evt.left_team_points ?? prev.left,
        right: evt.right_team_points ?? prev.right,
      }));
    },
    [answers, multiplier]
  );

  // ----- lifecycle -----
  useEffect(() => {
    sendMessage({ action: "roomStatus", roomId });
  }, [roomId, sendMessage]);

  // Seed from provider state if the status arrived before we mounted.
  useEffect(() => {
    if (!seeded.current && room && !roomData) {
      seeded.current = true;
      applyRoomStatus(room);
    }
  }, [room, roomData, applyRoomStatus]);

  // When the clock hits zero, the admin's client advances the round (once).
  useEffect(() => {
    if (timer !== 0 || deadline.current == null) return;
    deadline.current = null;
    if (isAdmin && !showResults && roomData) {
      sendMessage({ action: "nextQuestion", roomId });
    }
  }, [timer, isAdmin, showResults, roomData, roomId, sendMessage]);

  // Countdown ticks near the end of the answer window.
  useEffect(() => {
    if (phase === "answer" && timer <= 6 && timer > 1) sfx.tick();
  }, [phase, timer]);

  // Round intro sting
  const introPlayed = useRef(-1);
  useEffect(() => {
    if (phase === "intro" && introPlayed.current !== idx) {
      introPlayed.current = idx;
      sfx.roundIntro();
    }
  }, [phase, idx]);

  const submitAnswer = () => {
    const answer = answerText.trim();
    if (!answer) return;
    sendMessage({ action: "submitAnswer", roomId, userId, answer });
    setAnswerText("");
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    setMutedUi(next);
  };

  // ----- results screen -----
  if (phase === "results") {
    return (
      <ResultsView
        result={gameResult}
        players={players}
        myTeam={myTeam}
        isAdmin={isAdmin}
        onClose={() => {
          if (isAdmin) sendMessage({ action: "closeRoom", roomId });
          else navigate("/");
        }}
      />
    );
  }

  const answerPhase = phase === "answer";
  const mult =
    multiplier > 1 && idx < questionCount ? `${multiplier}X POINTS!` : null;

  return (
    <>
      {/* ---------- 3D stage: avatars, orbs, confetti ---------- */}
      <ArenaCanvas cameraBase={[0, 2.9, 8.8]} cameraLook={[0, 1.7, 0]} sway={0.6}>
        <group position={[0, 0, TEAM_Z]}>
          <TeamStage
            teamKey="left"
            players={leftTeam}
            bumps={bumps}
            highlightId={highlightId}
            showLabel={false}
          />
          <TeamStage
            teamKey="right"
            players={rightTeam}
            bumps={bumps}
            highlightId={highlightId}
            showLabel={false}
          />
        </group>
        {orbs.map((o) => (
          <Orb
            key={o.id}
            from={o.from}
            to={o.to}
            color={o.color}
            label={o.label}
            data={o.data}
            duration={1.05}
            onArrive={(evt) => handleOrbArrive(evt, o.id)}
          />
        ))}
        <ConfettiBurst trigger={burst} origin={[0, 5, -2.5]} />
      </ArenaCanvas>

      {/* ---------- HUD ---------- */}
      <div className="hud-top">
        <div className="chip">
          ROOM <span className="gold">{roomId}</span>
        </div>
        <div className="row">
          <div className="chip">
            {questionCount > 0 && idx < questionCount
              ? `ROUND ${idx + 1} / ${questionCount}`
              : "FINALE"}
            {mult && <span className="gold">· {mult}</span>}
          </div>
          <button className="icon-btn" onClick={toggleMute}>
            {muted ? "🔇" : "🔊"}
          </button>
        </div>
      </div>

      {/* ---------- crisp HTML board per phase ---------- */}
      {answerPhase && currentQuestion && (
        <HtmlBoard
          question={currentQuestion.question}
          answers={answers}
          revealed={revealed}
          multiplierLabel={mult ? `${multiplier}X` : null}
          flash={flash}
          leftPct={leftPct}
          rightPct={rightPct}
        />
      )}

      {phase === "summary" && prevQuestion && (
        <HtmlBoard
          kicker={`ROUND ${idx} — ALL THE ANSWERS`}
          question={prevQuestion.question}
          answers={prevQuestion.correctAnswers || []}
          revealed={(prevQuestion.correctAnswers || []).map(() => true)}
          showTug={false}
          footer={
            <div className="board-footer">
              <div className="round-score left">
                TITANS +{roomData?.left_team_prev_round_points ?? 0}
              </div>
              <div className="round-score right">
                VIPERS +{roomData?.right_team_prev_round_points ?? 0}
              </div>
            </div>
          }
        />
      )}

      {phase === "loading" && (
        <div className="center-screen">
          <div className="glass-note">
            <h2>ENTERING THE ARENA…</h2>
            <p>syncing with the game room</p>
          </div>
        </div>
      )}

      {phase === "intro" && (
        <div className="announce">
          <p className="big">ROUND {idx + 1}</p>
          <p className="sub">{mult || "GUESS THEIR ANSWERS!"}</p>
          <p className="tick">starting in {timer - 85}…</p>
        </div>
      )}

      {/* ---------- answer dock ---------- */}
      {answerPhase && (
        <div className="answer-dock">
          <TimerRing timer={timer} />
          {!roundWon ? (
            <>
              <input
                className="ainput"
                type="text"
                placeholder="TYPE YOUR GUESS AND HIT ENTER…"
                value={answerText}
                autoFocus
                onChange={(e) => setAnswerText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitAnswer()}
              />
              <button
                className={`abtn dock-send ${
                  myTeam === "right" ? "abtn-violet" : "abtn-lime"
                }`}
                onClick={submitAnswer}
              >
                FIRE!
              </button>
            </>
          ) : (
            <>
              <div className="dock-note">BOARD CLEARED!</div>
              {isAdmin && (
                <button
                  className="abtn dock-send abtn-gold"
                  onClick={() => sendMessage({ action: "nextQuestion", roomId })}
                >
                  NEXT ROUND →
                </button>
              )}
            </>
          )}
        </div>
      )}

      <ToastContainer />
    </>
  );
}

export default Game;
